import { Building2, SaudiRiyal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import type { GetRequest } from '@cold-ambulance/client';
import {
  Button,
  Card,
  CardContent,
  Skeleton,
  buttonVariants,
  cn,
} from '@cold-ambulance/shadcn';
import {
  ServiceBadge,
  formatDate,
  sumEquipmentQuantities,
} from '@cold-ambulance/ui';

function toEquipmentArray(value: unknown) {
  return Array.isArray(value) ? value : [];
}

export function OfferCard({
  offer,
  request,
  index,
}: {
  offer: GetRequest['offers'][number];
  request: GetRequest;
  index: number;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isAcceptedOffer = request.offerId === offer.id;

  const ambulanceWithDriverCount = request.ambulanceServiceWithDriver.reduce(
    (acc, service) =>
      acc + service.type1Amount + service.type2Amount + service.type3Amount,
    0,
  );
  const ambulanceWithoutDriverCount =
    request.ambulanceServiceWithoutDriver.reduce(
      (acc, service) =>
        acc + service.type1Amount + service.type2Amount + service.type3Amount,
      0,
    );
  const ambulanceCount = ambulanceWithDriverCount + ambulanceWithoutDriverCount;

  const equipmentCount =
    request.ambulanceServiceWithDriver.reduce(
      (acc, service) =>
        acc +
        sumEquipmentQuantities(toEquipmentArray(service.type1Equipment)) +
        sumEquipmentQuantities(toEquipmentArray(service.type2Equipment)) +
        sumEquipmentQuantities(toEquipmentArray(service.type3Equipment)),
      0,
    ) +
    request.ambulanceServiceWithoutDriver.reduce(
      (acc, service) =>
        acc +
        sumEquipmentQuantities(toEquipmentArray(service.type1Equipment)) +
        sumEquipmentQuantities(toEquipmentArray(service.type2Equipment)) +
        sumEquipmentQuantities(toEquipmentArray(service.type3Equipment)),
      0,
    ) +
    request.ambulance4x4WithDriver.reduce(
      (acc, service) =>
        acc + sumEquipmentQuantities(toEquipmentArray(service.equipment)),
      0,
    ) +
    request.ambulance4x4WithoutDriver.reduce(
      (acc, service) =>
        acc + sumEquipmentQuantities(toEquipmentArray(service.equipment)),
      0,
    ) +
    sumEquipmentQuantities(toEquipmentArray(request.medicalEquipment));

  const paramedicCount = request.requestParamedicService.reduce(
    (acc, service) => acc + service.amount,
    0,
  );

  const nurseCount = request.requestNurseService.reduce(
    (acc, service) => acc + service.amount,
    0,
  );

  return (
    <Card className="flex min-h-64 w-full flex-col justify-end rounded-xl border border-[#DFDFDF] px-9 py-7">
      <CardContent className="flex h-full flex-col justify-center gap-4 p-0">
        <div className="flex flex-col items-center gap-4">
          <div className="flex w-full items-center justify-between gap-4">
            <div className="flex flex-1 items-center gap-2.5">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#337540]">
                <Building2 className="h-3.5 w-3.5 text-white" />
              </div>
              <div className="flex flex-col">
                <h3 className="text-lg font-bold">
                  {'name' in offer.vendor
                    ? offer.vendor.name
                    : `${t('offers.offerProvider')} ${index}`}
                </h3>
                {'contact' in offer.vendor && offer.vendor.contact && (
                  <p className="text-muted-foreground text-sm">
                    {offer.vendor.contact.firstName}{' '}
                    {offer.vendor.contact.lastName}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="flex w-full items-center gap-2">
            {ambulanceCount > 0 && (
              <ServiceBadge>
                <span className="text-center text-sm font-medium">
                  {ambulanceCount} {t('services.ambulance')}
                  {equipmentCount > 0 && (
                    <span className="text-muted-foreground ms-1 text-xs font-normal">
                      ({equipmentCount} {t('services.equipment')})
                    </span>
                  )}
                </span>
              </ServiceBadge>
            )}
            {paramedicCount > 0 && (
              <ServiceBadge>
                <span className="text-center text-sm font-medium">
                  {paramedicCount} {t('services.paramedic')}
                </span>
              </ServiceBadge>
            )}
            {nurseCount > 0 && (
              <ServiceBadge>
                <span className="text-center text-sm font-medium">
                  {nurseCount} {t('services.nurse')}
                </span>
              </ServiceBadge>
            )}
          </div>
        </div>

        <div className="flex w-full items-center justify-between gap-2">
          <span className="text-right">{t('offers.offeredPrice')}</span>
          <div className="flex items-center gap-2">
            <span className="font-bold">{offer.price.toLocaleString()}</span>
            <SaudiRiyal />
          </div>
        </div>

        <div className="flex w-full items-center justify-between gap-2">
          <span className="text-right">{t('offers.submissionDate')}</span>
          <div className="flex items-center gap-2">
            <span className="font-bold">{formatDate(offer.createdAt)}</span>
          </div>
        </div>

        <Button
          onClick={() => {
            if (isAcceptedOffer) {
              navigate(`/requests/${request.id}/offers/${offer.id}/contract`);
            } else {
              navigate(`/requests/${request.id}/offers/${offer.id}/accept`);
            }
          }}
          className={buttonVariants({
            variant: 'default',
            className: cn('!h-11 w-full px-4'),
          })}
        >
          {t('offers.offerDetails')}
        </Button>
      </CardContent>
    </Card>
  );
}

export function OfferCardSkeleton() {
  return (
    <div className="rounded-lg border bg-white p-4">
      <div className="h-full space-y-3">
        <Skeleton className="h-4 w-40" />
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="flex items-center gap-0.5 rounded-md border bg-white">
            <Skeleton className="h-3 w-8" />
          </span>
          <span className="flex items-center gap-0.5 rounded-md border bg-white">
            <Skeleton className="h-3 w-8" />
          </span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-16" />
        </div>
        <div className="flex items-center justify-between gap-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-16" />
        </div>
        <Skeleton className="h-10 w-full rounded-md" />
      </div>
    </div>
  );
}
